/**
 * Image model registry — single source of truth for the prompt-to-image
 * generation models offered in Editor v2.
 *
 * IMPORTANT:
 * - All generation models here run on REPLICATE. Google AI Studio is only used
 *   for chat-based refinement (ai-designer), because chat history upload is
 *   only supported there — it is intentionally NOT part of this registry.
 * - The model `id` IS the `modelOverride` string sent to the backend and matched
 *   in app/api/tools/generate-image/route.ts.
 * - This file is shared by both client components (UI chooser) and the API route
 *   (credit calculation), so it must stay free of any server-only imports.
 * - GPT Image Replicate switches (moderation / quality) live in libs/replicate.ts
 *   as globals — they are not per-model or per-request.
 */

export type ImageResolution = "1K" | "2K" | "4K";

export type GptImageReplicateModel =
  | "openai/gpt-image-2"
  | "openai/gpt-image-2.5-flare";

export type ImageModelBackend = "gpt-image" | "nano-banana" | "nano-banana-2";

// The id doubles as the `modelOverride` value handled by the generate-image route.
export type ImageModelId =
  | "pfpmaker" // Branded default — routes to openai/gpt-image-2.5-flare
  | "replicate-gpt-image-2.5-flare"
  | "replicate-gpt-image-2"
  | "replicate-nano-banana" // Hidden from the chooser; API still accepts it
  | "replicate-nano-banana-2";

export interface ImageModelDef {
  id: ImageModelId;
  /** User-facing name shown in the chooser. */
  name: string;
  /** Short pill label, e.g. "BALANCED" / "BEST" / "HOT". */
  tag?: string;
  /** Visual tone for the tag pill. */
  tagTone?: "balanced" | "pro" | "new" | "fast" | "hot" | "good";
  description: string;
  /** Path to the model's SVG icon under /public. */
  icon: string;
  /** Tailwind classes for the icon badge background. */
  iconClassName: string;
  isDefault?: boolean;
  /** Omit from the Editor v2 chooser while keeping the id valid on the API. */
  hiddenFromChooser?: boolean;
  /** Which Replicate generator the API route should call. */
  backend: ImageModelBackend;
  /** Replicate slug when `backend` is `gpt-image`. */
  replicateModel?: GptImageReplicateModel;
  /**
   * Resolutions offered to the user, in display order.
   * Resolution affects credit cost (see `creditCosts`).
   */
  resolutions: ImageResolution[];
  /**
   * Credit cost per resolution. Must contain an entry for every value in
   * `resolutions`. Aspect ratio selection does NOT affect credit cost.
   */
  creditCosts: Record<ImageResolution, number>;
  /**
   * Aspect ratios offered in the UI, in display order.
   * "match_input_image" and "auto" are rendered as "Auto".
   */
  aspectRatios: string[];
  /** Preferred default aspect ratio. 9:16 unless the model can't do it. */
  defaultAspectRatio: string;
}

// Curated aspect-ratio sets per model (subset of what each model supports).
const NANO_BANANA_UI_RATIOS = [
  "match_input_image",
  "1:1",
  "2:3",
  "3:2",
  "3:4",
  "4:3",
  "9:16",
  "16:9",
];

// openai/gpt-image-2 and gpt-image-2.5-flare share this Replicate aspect set.
const GPT_IMAGE_UI_RATIOS = [
  "1:1",
  "3:2",
  "2:3",
  "4:3",
  "3:4",
  "16:9",
  "9:16",
  "auto",
];

export const IMAGE_MODELS: ImageModelDef[] = [
  {
    id: "pfpmaker",
    name: "PFPMaker v1",
    tag: "BALANCED",
    tagTone: "balanced",
    description:
      "Balanced speed and quality, perfect for everyday profile pictures.",
    icon: "/assets/images/models/pfpmaker.svg",
    iconClassName: "bg-white border border-gray-200",
    isDefault: true,
    backend: "gpt-image",
    replicateModel: "openai/gpt-image-2.5-flare",
    resolutions: ["1K"],
    creditCosts: { "1K": 2, "2K": 2, "4K": 2 },
    aspectRatios: GPT_IMAGE_UI_RATIOS,
    defaultAspectRatio: "9:16",
  },
  {
    id: "replicate-gpt-image-2.5-flare",
    name: "GPT Image 2.5",
    tag: "NEW",
    tagTone: "new",
    description:
      "OpenAI's newest image model with best character preservation.",
    icon: "/assets/images/models/openai.svg",
    iconClassName: "bg-white border border-gray-200",
    backend: "gpt-image",
    replicateModel: "openai/gpt-image-2.5-flare",
    resolutions: ["1K"],
    creditCosts: { "1K": 3, "2K": 3, "4K": 3 },
    aspectRatios: GPT_IMAGE_UI_RATIOS,
    defaultAspectRatio: "9:16",
  },
  {
    id: "replicate-gpt-image-2",
    name: "GPT Image 2",
    tag: "BEST",
    tagTone: "pro",
    description:
      "OpenAI's lastest image model with stronger instruction following.",
    icon: "/assets/images/models/openai.svg",
    iconClassName: "bg-white border border-gray-200",
    backend: "gpt-image",
    replicateModel: "openai/gpt-image-2",
    resolutions: ["1K"],
    creditCosts: { "1K": 3, "2K": 3, "4K": 3 },
    aspectRatios: GPT_IMAGE_UI_RATIOS,
    defaultAspectRatio: "2:3",
  },
  {
    id: "replicate-nano-banana",
    name: "Nano Banana",
    tag: "FAST",
    tagTone: "fast",
    description:
      "Fast, reliable image generation with good quality at 1K resolution.",
    icon: "/assets/images/models/nano-banana.svg",
    iconClassName: "bg-amber-50 border border-amber-100",
    hiddenFromChooser: true,
    backend: "nano-banana",
    resolutions: ["1K"],
    creditCosts: { "1K": 2, "2K": 2, "4K": 2 },
    aspectRatios: NANO_BANANA_UI_RATIOS,
    defaultAspectRatio: "9:16",
  },
  {
    id: "replicate-nano-banana-2",
    name: "Nano Banana 2",
    tag: "GOOD",
    tagTone: "good",
    description:
      "High fidelity model with multi-resolution output. Great for crisp, detailed results.",
    icon: "/assets/images/models/nano-banana.svg",
    iconClassName: "bg-amber-50 border border-amber-100",
    backend: "nano-banana-2",
    resolutions: ["1K", "2K", "4K"],
    creditCosts: { "1K": 4, "2K": 6, "4K": 8 },
    aspectRatios: NANO_BANANA_UI_RATIOS,
    defaultAspectRatio: "9:16",
  },
];

export const DEFAULT_IMAGE_MODEL_ID: ImageModelId = "pfpmaker";

export const CHOOSER_IMAGE_MODELS: ImageModelDef[] = IMAGE_MODELS.filter(
  (m) => !m.hiddenFromChooser
);

/** Look up a model definition by id. Returns undefined for unknown/legacy ids. */
export function getImageModel(id?: string | null): ImageModelDef | undefined {
  if (!id) return undefined;
  return IMAGE_MODELS.find((m) => m.id === id);
}

/** True when the given id is one of the Editor v2 registry models. */
export function isRegistryModel(id?: string | null): id is ImageModelId {
  return !!getImageModel(id);
}

/**
 * Resolve the credit cost for a model at a given resolution.
 * Falls back to the cost of the model's first (lowest) resolution when the
 * requested resolution isn't offered by the model.
 */
export function getModelCreditCost(
  id: ImageModelId,
  resolution?: ImageResolution | string | null
): number {
  const model = getImageModel(id);
  if (!model) return 0;

  const res = (resolution as ImageResolution) ?? model.resolutions[0];
  if (model.resolutions.includes(res) && model.creditCosts[res] != null) {
    return model.creditCosts[res];
  }
  // Unknown/unsupported resolution → charge the model's base (lowest) tier.
  return model.creditCosts[model.resolutions[0]];
}

/**
 * Clamp an aspect ratio to one the model supports, falling back to the model's
 * default. Used so the UI never holds an aspect the active model can't render.
 */
export function resolveModelAspectRatio(
  id: ImageModelId,
  aspectRatio?: string | null
): string {
  const model = getImageModel(id);
  if (!model) return aspectRatio || "9:16";
  return aspectRatio && model.aspectRatios.includes(aspectRatio)
    ? aspectRatio
    : model.defaultAspectRatio;
}

/**
 * Clamp a resolution to one the model supports, falling back to the model's
 * base (first) resolution.
 */
export function resolveModelResolution(
  id: ImageModelId,
  resolution?: string | null
): ImageResolution {
  const model = getImageModel(id);
  if (!model) return (resolution as ImageResolution) || "1K";
  return resolution && model.resolutions.includes(resolution as ImageResolution)
    ? (resolution as ImageResolution)
    : model.resolutions[0];
}
